import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import _, { sortBy } from 'lodash';
import { useReceiptGenerator, ReceiptType, useDispatch, useI18n, useSelector } from '~Hooks';
import { removeToast } from '~/actions';
import { useStorage } from '~Components/Storage';
import { useReportsApi } from '~Components/ReportsUniverse';
import { PrintRow } from '~Components/PrinterManager';
import { receiptLinesToPrintRows } from '~Hooks/Entities/useReceipt';
import { sortByCourseTypeOrder } from '~Utils/sortArticles';
import { useApolloClient } from '@apollo/client';
import { useMemo } from 'react';
import { groupOfferMatches } from '~Utils/offers';

interface Props {
  selectedCheque: string;
  selectedTable: { id: string; name: string } | null;
}

export const usePrintBill = ({ selectedCheque, selectedTable }: Props) => {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const api = useTableApi();
  const reportsApi = useReportsApi();
  const [storage] = useStorage();
  const universe = useTableUniverse();
  const { i18n } = useI18n();

  const country = useSelector((state) => state.country);

  const [, , venueId] = location.hash.split('/');
  const generateReceipt = useReceiptGenerator({ venueId });

  const cheque = universe.cheques.find((item) => item.id === selectedCheque);
  const chequeItems = universe.items.filter((item) => item.chequeId === selectedCheque);
  const firstCheque = universe.cheques.find((item) => item.tableId === selectedTable?.id);
  const coverage = firstCheque ? firstCheque.coverage : '';

  const sortedItems = sortByCourseTypeOrder(chequeItems, client);

  // Group items by offers to show orange grouped headers
  // Uses Tables-specific utility that works directly with TableItem (no conversion needed)
  const grouped = useMemo(() => {
    if (!venueId || !storage.receiptShowOnlyOfferName) {
      return { matches: [], unmatched: sortedItems };
    }
    return groupOfferMatches(sortedItems, venueId, client, i18n);
  }, [sortedItems, venueId, client, i18n]);

  const handlePrint = async (receiptType: ReceiptType) => {
    if (chequeItems?.length < 1) return;

    const items = grouped.unmatched.map((item) => ({
      entryId: item.itemId,
      amount: item.amount,
      discountTitle: item.discountTitle || undefined,
      discount: item.discount,
      percentageVAT: item.vatRate,
      variations: item.variations?.length ? item.variations : [],
      title: item.title,
      fraction: item.fraction,
    }));

    const offerItems = grouped.matches.reduce((acc, offer) => {
      const totalPrice = offer.items.reduce((sum, item) => sum + item.amount, 0);
      const offerDiscount = offer.items.reduce((sum, item) => sum + item.discount, 0);

      const offerHeader = {
        entryId: `offer-${offer.offerId}`,
        amount: totalPrice,
        discount: offerDiscount,
        discountTitle: i18n('Receipt.Discount') || undefined, // Since the offer name is shown in the title we just add the generic "Discount" text here to avoid redundancy
        percentageVAT: offer.items[0].vatRate,
        variations: [],
        title: offer.offerName,
        fraction: 1,
      };

      return [...acc, offerHeader];
    }, [] as typeof items);

    const totalDiscount = _.sumBy([...offerItems, ...items], (item) => item.discount);

    const sortedItemsIncOfferItems = sortBy([...offerItems, ...items], (item) =>
      item.title.toLowerCase(),
    );

    const itemsIncServiceCharge = [
      ...sortedItemsIncOfferItems,
      ...(cheque?.serviceCharge
        ? [
            {
              entryId: cheque.serviceCharge.entryId,
              amount: cheque.serviceCharge.amount,
              discount: 0,
              percentageVAT: cheque.serviceCharge.vatRate,
              variations: [],
              title: i18n('ServiceCharge.Title') + ` ${cheque.serviceCharge.percentage}%`,
              fraction: 1,
            },
          ]
        : []),
    ];

    const receipt = await generateReceipt({
      items: itemsIncServiceCharge,
      totalDiscount,
      type: receiptType,
      isTable: true,
      extraVars: {
        datetime: new Date().toISOString(),
        tableCode: selectedTable?.name || '--',
        covers: coverage?.toString() || '--',
      },
      userId: country === 'ES' ? cheque?.userId ?? undefined : undefined,
    });

    if (receipt) {
      const message: PrintRow[] = receiptLinesToPrintRows(receipt.lines);

      /**
       * This is used for Brasseriegruppens
       * "Seamless Payments" solution.
       *
       * If the "printQROnCheque" setting is true,
       * we will print a QR code on the receipt that
       * can then be scanned by the BG app and paid for.
       */
      const QRContent = storage.printQROnCheque
        ? `brasseriegruppen://pay-cheque?id=${selectedCheque}`
        : undefined;

      if (QRContent) {
        message.push({
          content: QRContent,
          qr: true,
        });
      }

      window.printerManager?.initiatePrint({
        data: message,
        printType: 'RECEIPT',
        base64Image: receipt.base64Image,
        receipt: receipt.document,
      });

      dispatch(removeToast(selectedCheque));
    }

    api.markPrinted(selectedCheque);
    api.log.markPrinted(selectedCheque);
    reportsApi.provisionalReceiptPrinted(cheque?.amount ?? 0);
  };

  const printBill = () => handlePrint(ReceiptType.Cheque);
  const printInvoiceBill = () => handlePrint(ReceiptType.Invoice);

  return {
    printBill,
    printInvoiceBill,
  };
};
